import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Grid, FormControl, InputLabel, Select, MenuItem, Typography, Checkbox, FormControlLabel, Tabs, Tab, CircularProgress, Card, CardContent, Chip, Stack } from '@mui/material';
import { FetchRoles, FetchOrganizations, FetchPermissions, CreateRole } from '../../../../services/api';
import CustomAlert from '../../../../components/CustomAlert/CustomAlert';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const CreateRoleForm = ({ onClose, onSuccess }) => {
    const [permissionTab, setPermissionTab] = useState(0);
    const [permissions, setPermissions] = useState([]);
    const [startLoading, setStartLoading] = useState(false);

    // Master data
    const [organizations, setOrganizations] = useState([]);

    // Alert state
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState('success');

    const token = localStorage.getItem('accessToken');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setStartLoading(true);
        try {
            const [permResp, orgResp] = await Promise.all([
                FetchPermissions({ limit: 300 }, token),
                FetchOrganizations(token)
            ]);

            if (permResp?.data?.permissions) setPermissions(permResp.data.permissions);
            else if (permResp?.data?.data) setPermissions(permResp.data.data);
            if (orgResp?.data?.data) setOrganizations(orgResp.data.data);

        } catch (error) {
            console.error("Error loading data", error);
        }
        setStartLoading(false);
    };

    const validationSchema = Yup.object({
        name: Yup.string().trim().min(2, 'Role name must be at least 2 characters').max(100, 'Role name too long').required('Role Name is required'),
        description: Yup.string().trim().max(500, 'Description too long').required('Description is required'),
        organizationId: Yup.string().nullable(),
        roleCategory: Yup.string().required('Category is required'),
        // Permissions optional? Usually a role needs permissions, but backend might allow empty.
        // Let's not enforce min 1 for now unless requested, but usually roles have permissions.
        selectedPermissions: Yup.array().of(Yup.string())
    });

    const formik = useFormik({
        initialValues: {
            name: '',
            description: '',
            roleCategory: 'internal',
            organizationId: '',
            selectedPermissions: [],
            applicationScope: 'global' // Default if needed, though not in form UI originally but in payload
        },
        validationSchema: validationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setSubmitting(true);
            const payload = {
                name: values.name,
                description: values.description,
                roleCategory: values.roleCategory,
                applicationScope: 'global', // Hardcoded as per original
                organizationId: values.organizationId || null,
                permissionIds: values.selectedPermissions,
                isActive: true
            };

            try {
                const resp = await CreateRole(payload, token);
                if (resp?.data) {
                    setAlertMessage('Role created successfully');
                    setAlertSeverity('success');
                    setAlertOpen(true);
                    setTimeout(() => {
                        if (onSuccess) onSuccess();
                        if (onClose) onClose();
                    }, 1500);
                } else {
                    setAlertMessage(resp?.response?.data?.message || 'Failed to create role');
                    setAlertSeverity('error');
                    setAlertOpen(true);
                }
            } catch (error) {
                setAlertMessage('An unexpected error occurred');
                setAlertSeverity('error');
                setAlertOpen(true);
            } finally {
                setSubmitting(false);
            }
        },
    });

    // Permission Logic
    // Group permissions by module or resource
    const groupedPermissions = permissions.reduce((acc, perm) => {
        const group = perm.module || perm.resource || 'Other';
        if (!acc[group]) acc[group] = [];
        acc[group].push(perm);
        return acc;
    }, {});

    const permissionGroups = Object.keys(groupedPermissions);

    const handlePermissionToggle = (permId) => {
        const currentSelected = formik.values.selectedPermissions;
        if (currentSelected.includes(permId)) {
            formik.setFieldValue('selectedPermissions', currentSelected.filter(id => id !== permId));
        } else {
            formik.setFieldValue('selectedPermissions', [...currentSelected, permId]);
        }
    };

    const handleSelectAllGroup = (group) => {
        const groupPermIds = groupedPermissions[group].map(p => p.id);
        const currentSelected = formik.values.selectedPermissions;
        const allSelected = groupPermIds.every(id => currentSelected.includes(id));

        if (allSelected) {
            // Deselect all
            formik.setFieldValue('selectedPermissions', currentSelected.filter(id => !groupPermIds.includes(id)));
        } else {
            // Select all
            const newSelected = [...currentSelected];
            groupPermIds.forEach(id => {
                if (!newSelected.includes(id)) newSelected.push(id);
            });
            formik.setFieldValue('selectedPermissions', newSelected);
        }
    };

    if (startLoading) return <CircularProgress />;

    return (
        <Card sx={{ height: '80vh', display: 'flex', flexDirection: 'column' }}>
            <CustomAlert open={alertOpen} handleClose={() => setAlertOpen(false)} message={alertMessage} severity={alertSeverity} />

            <Box p={2} borderBottom={1} borderColor="divider">
                <Typography variant="h6">Create New Role</Typography>
            </Box>

            <CardContent sx={{ flex: 1, overflowY: 'auto' }}>
                <form onSubmit={formik.handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                id="name"
                                name="name"
                                label="Role Name *"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.name && Boolean(formik.errors.name)}
                                helperText={formik.touched.name && formik.errors.name}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                id="description"
                                name="description"
                                label="Description *"
                                value={formik.values.description}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.description && Boolean(formik.errors.description)}
                                helperText={formik.touched.description && formik.errors.description}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Organization</InputLabel>
                                <Select
                                    id="organizationId"
                                    name="organizationId"
                                    value={formik.values.organizationId}
                                    label="Organization"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                >
                                    <MenuItem value=""><em>None (Global)</em></MenuItem>
                                    {organizations.map(org => (
                                        <MenuItem key={org.id} value={org.id}>{org.organizationName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Category</InputLabel>
                                <Select
                                    id="roleCategory"
                                    name="roleCategory"
                                    value={formik.values.roleCategory}
                                    label="Category"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                >
                                    <MenuItem value="internal">Internal</MenuItem>
                                    <MenuItem value="external">External</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            {/* Role Level removed */}
                        </Grid>
                    </Grid>

                    <Box mt={3}>
                        <Typography variant="subtitle1" gutterBottom>Selected Permissions ({formik.values.selectedPermissions.length})</Typography>
                        <Box display="flex" flexWrap="wrap" gap={1} mb={2} p={1} bgcolor="#f5f5f5" borderRadius={1} minHeight="50px">
                            {formik.values.selectedPermissions.length === 0 && <Typography variant="body2" color="textSecondary">No permissions selected</Typography>}
                            {permissions.filter(p => formik.values.selectedPermissions.includes(p.id)).map(perm => (
                                <Chip
                                    key={perm.id}
                                    label={`${perm.module} : ${perm.action}`}
                                    onDelete={() => handlePermissionToggle(perm.id)}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                />
                            ))}
                        </Box>
                        <Typography variant="subtitle1">Available Permissions</Typography>
                        <Tabs value={permissionTab} onChange={(e, v) => setPermissionTab(v)} variant="scrollable">
                            {permissionGroups.map((group, index) => (
                                <Tab key={group} label={group} />
                            ))}
                        </Tabs>
                        <Box p={2} border={1} borderColor="divider">
                            {permissionGroups.map((group, index) => (
                                <div key={group} role="tabpanel" hidden={permissionTab !== index}>
                                    {permissionTab === index && (
                                        <Box>
                                            <Button size="small" onClick={() => handleSelectAllGroup(group)}>
                                                Select/Deselect All
                                            </Button>
                                            <Grid container>
                                                {groupedPermissions[group].map(perm => (
                                                    <Grid item xs={12} sm={6} md={4} key={perm.id}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={formik.values.selectedPermissions.includes(perm.id)}
                                                                    onChange={() => handlePermissionToggle(perm.id)}
                                                                />
                                                            }
                                                            label={`${perm.action} - ${perm.description || perm.name}`}
                                                        />
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Box>
                                    )}
                                </div>
                            ))}
                        </Box>
                    </Box>
                </form>
            </CardContent>

            <Box p={2} borderTop={1} borderColor="divider" display="flex" justifyContent="flex-end" gap={2}>
                <Button onClick={onClose} variant="outlined">Cancel</Button>
                <Button
                    onClick={formik.handleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={formik.isSubmitting || !formik.isValid}
                >
                    Create Role
                </Button>
            </Box>
        </Card>
    );
};

export default CreateRoleForm;
